import { useTranslation } from 'react-i18next';

import { toQueryFilters, useLogsQuery } from '../hooks/use-logs';
import { useLogInquiryListStore } from '../store/log-inquiry-list.store';
import {
  LOG_PAGE_SIZE,
  LogInquiryApiError,
  type LogInquiryApiErrorCode,
} from '../types/log.types';
import {
  LogInquiryEmpty,
  LogInquirySkeleton,
  LogInquiryTable,
} from './LogInquiryTable';

// Validation errors carry a user-actionable message (e.g. an out-of-order date
// range the BE re-rejected) and are surfaced verbatim. Every other failure —
// system / network / unknown — shows the generic retrieval-failure message
// (PRD 18 §8.2: "로그를 불러올 수 없습니다. 잠시 후 다시 시도해주세요."). Without
// this split the adapter's blanket LogInquiryApiError wrapping would show the raw
// "알 수 없는 오류가 발생했습니다." for a 500, so the spec message never rendered.
const VALIDATION_ERROR_CODES: ReadonlySet<LogInquiryApiErrorCode> = new Set([
  'INVALID_DATE_RANGE',
  'INVALID_DATE_FORMAT',
  'AUDIT_DATE_RANGE_INVALID',
  // A filter carrying a value the server rejects. Without this the screen would show the
  // generic "could not load the log" text, which points the user at the wrong problem.
  'AUDIT_FILTER_VALUE_INVALID',
]);

function LogInquiryError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 8,
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('logInquiry.actions.retry')}
      </button>
    </div>
  );
}

// How many numbered buttons the bar shows at once. With 20 rows per page a
// window of 5 keeps the bar narrow while still letting the user jump a few
// pages at a time; 이전 / 다음 walk the window along.
const PAGE_WINDOW = 5;

// The page numbers to draw for the current position: a sliding window of at
// most PAGE_WINDOW pages, kept inside [0, totalPages).
export function pageWindow(current: number, totalPages: number): number[] {
  if (totalPages <= 0) return [];
  const start = Math.max(
    0,
    Math.min(current - Math.floor(PAGE_WINDOW / 2), totalPages - PAGE_WINDOW),
  );
  const end = Math.min(totalPages, start + PAGE_WINDOW);
  const pages: number[] = [];
  for (let p = start; p < end; p += 1) pages.push(p);
  return pages;
}

// 이전 / 1 2 3 / 다음 — the numbered pagination bar the customer document asks
// for (PRD 18 §2.2 item 11). Buttons carry the human page number (1-based)
// while the wire page index stays zero-based.
function LogInquiryPagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation('admin');
  if (totalPages <= 1) return null;

  const atFirst = page <= 0;
  const atLast = page >= totalPages - 1;

  return (
    <nav
      aria-label={t('logInquiry.list.pagination.label')}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        marginTop: 16,
      }}
    >
      <button
        type="button"
        className="btn btn-sm btn-outline"
        onClick={() => onPageChange(page - 1)}
        disabled={atFirst}
      >
        {t('logInquiry.actions.previousPage')}
      </button>
      {pageWindow(page, totalPages).map((p) => (
        <button
          key={p}
          type="button"
          className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline'}`}
          aria-current={p === page ? 'page' : undefined}
          onClick={() => onPageChange(p)}
        >
          {p + 1}
        </button>
      ))}
      <button
        type="button"
        className="btn btn-sm btn-outline"
        onClick={() => onPageChange(page + 1)}
        disabled={atLast}
      >
        {t('logInquiry.actions.nextPage')}
      </button>
    </nav>
  );
}

export function LogInquiryListView() {
  const { t } = useTranslation('admin');
  const filters = useLogInquiryListStore((s) => s.filters);
  const page = useLogInquiryListStore((s) => s.page);
  const sort = useLogInquiryListStore((s) => s.sort);
  const dir = useLogInquiryListStore((s) => s.dir);
  const setPage = useLogInquiryListStore((s) => s.setPage);
  const toggleSort = useLogInquiryListStore((s) => s.toggleSort);

  // 'all'/empty filter values are normalized to undefined so the wire call never
  // receives the FE placeholder (shared with the CSV export — see toQueryFilters).
  const queryFilters = toQueryFilters(filters);

  const { data, isLoading, error, refetch } = useLogsQuery(
    queryFilters,
    page,
    LOG_PAGE_SIZE,
    sort,
    dir,
  );

  if (isLoading) return <LogInquirySkeleton />;

  if (error) {
    const message =
      error instanceof LogInquiryApiError && VALIDATION_ERROR_CODES.has(error.code)
        ? // A validation error (e.g. out-of-order date range) the BE re-rejects
          // even though the FE pre-validates: surface its specific message.
          error.message
        : t('logInquiry.list.loadError');
    return <LogInquiryError message={message} onRetry={() => void refetch()} />;
  }

  const rows = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;
  const sortState = { sort, dir, onSort: toggleSort };

  // The headers stay clickable on an empty result so the user can undo a sort
  // that produced nothing without first clearing their filters.
  if (rows.length === 0) return <LogInquiryEmpty sortState={sortState} />;

  return (
    <>
      <LogInquiryTable logs={rows} sortState={sortState} />
      <LogInquiryPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}
